import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "@material-ui/core";
import ColumnFilterPopover from "components/common/ColumnFilterPopover";
import NavigoAPIServices from "../services/navigoAPIServices";
import funnelIcon from "../../../../../assets/icons/funnel.svg";
import filterApplied from "../../../../../assets/icons/filter-applied-icon.svg";
import eyeOrangeIcon from "../../../../../assets/icons/eye-orange.svg";
import backArrowIcon from "../../../../../assets/icons/back_left_arrow_orange.png";
import ClaimStatusCell from "./ClaimStatusCell";
import AuthenticationService from "service/auth/AuthenticationService";
import EventService from "../../../../../service/event/EventService";
import "../styles/claimDashboard.scss";

const api = new NavigoAPIServices();
const PAGE_SIZE = 30;

const STATUS_LABEL = {
  1: "Received", 2: "Approved", 3: "Rejected", 4: "Processed",
  5: "Appealed", 6: "On Hold", 7: "Provisionally Approved", 8: "Resubmitted",
  9: "Draft",
};
const STATUS_OPTIONS = Object.keys(STATUS_LABEL).map((k) => ({ id: k, label: STATUS_LABEL[k] }));
// AI suggestion pill — handles both the short forms (APPROVE/REJECT/SKIP) and the
// raw decision words (APPROVED/REJECTED/SKIPPED/ON_HOLD). SKIPPED must NOT fall
// through to "Reject".
const SUGG_PILL = {
  APPROVE: { cls: "cd-sugg-approve", label: "Approve" },
  APPROVED: { cls: "cd-sugg-approve", label: "Approve" },
  REJECT: { cls: "cd-sugg-reject", label: "Reject" },
  REJECTED: { cls: "cd-sugg-reject", label: "Reject" },
  SKIP: { cls: "cd-sugg-skipped", label: "Skipped" },
  SKIPPED: { cls: "cd-sugg-skipped", label: "Skipped" },
  ON_HOLD: { cls: "cd-sugg-hold", label: "On hold" },
  ONHOLD: { cls: "cd-sugg-hold", label: "On hold" },
};
const SUGGESTION_OPTIONS = [
  { id: "APPROVE", label: "Approve" },
  { id: "REJECT", label: "Reject" },
  { id: "SKIP", label: "Skipped" },
  // A suspected AI-generated/tampered document rejects the claim rather than
  // holding it, but the aggregator still returns ON_HOLD for a blocking rule
  // failure a human can resolve (missing document, unreadable stamp), so the
  // suggestion — and this filter — stay in use.
  { id: "ON_HOLD", label: "On hold" },
];
// User type (patient profile type) — mirrors the legacy claims dashboard's
// getProfileIcon/getProfileLabel so the column shows a colored person icon + label.
const PROFILE_BG = { 0: "#9e9e9e", 1: "#b8893f", 2: "#f4b400", 3: "#9c8df2", 4: "#e57373" };
const PROFILE_LABEL = {
  0: "Default User",
  1: "Premium User",
  2: "HR Representative",
  3: "QA User",
  4: "Sensitive User",
};
// Filter options for the User type column. The ids ARE the profile types the wallet
// filters on — "Default User" keeps its own encoding of 0 (no mapping doc), so the
// order below is by tag prominence rather than by id.
const PROFILE_TYPE_OPTIONS = [1, 2, 3, 4, 0].map((t) => ({
  id: String(t),
  label: PROFILE_LABEL[t],
}));

const rupees = (v) => (v === null || v === undefined || v === 0 ? "-" : "₹" + Number(v).toLocaleString("en-IN"));
const fmtDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch (e) { return "-"; }
};
// The page number, column filters, date ranges and searches are mirrored into the
// query string. Opening a claim unmounts this component, so without that the view
// rebuilt itself from scratch on the way back (page 1, no filters); with it, the
// back navigation — and a refresh, or a link shared with a colleague — restores
// exactly what was on screen.
const readQueryState = (locationSearch) => {
  const q = new URLSearchParams(locationSearch || "");
  const range = (from, to) => ({ from: q.get(from) || "", to: q.get(to) || "" });
  let filters = {};
  try {
    const raw = q.get("filters");
    const parsed = raw ? JSON.parse(raw) : {};
    Object.keys(parsed || {}).forEach((key) => {
      const f = parsed[key];
      const values = (f && Array.isArray(f.values) ? f.values : []).filter(
        (v) => v && v.id !== undefined && v.id !== null,
      );
      if (values.length)
        filters[key] = { values, mode: f.mode === "exclude" ? "exclude" : "include" };
    });
  } catch (e) {
    filters = {}; // hand-edited/garbled param — fall back to no filters
  }
  const page = parseInt(q.get("page"), 10);
  return {
    pageNum: page > 0 ? page : 1,
    filters,
    received: range("receivedFrom", "receivedTo"),
    updated: range("updatedFrom", "updatedTo"),
    search: {
      patientId: q.get("patientId") || "",
      claimId: q.get("claimId") || "",
      patientName: q.get("patientName") || "",
    },
  };
};

const buildQueryString = (st) => {
  const q = new URLSearchParams();
  if (st.pageNum > 1) q.set("page", String(st.pageNum));
  if (st.search.patientId) q.set("patientId", st.search.patientId);
  if (st.search.claimId) q.set("claimId", st.search.claimId);
  if (st.search.patientName) q.set("patientName", st.search.patientName);
  if (st.received.from) q.set("receivedFrom", st.received.from);
  if (st.received.to) q.set("receivedTo", st.received.to);
  if (st.updated.from) q.set("updatedFrom", st.updated.from);
  if (st.updated.to) q.set("updatedTo", st.updated.to);
  // Labels ride along with the ids so the applied-filter chips still read
  // correctly on a cold load, before the async option lists come back.
  const active = {};
  Object.keys(st.filters || {}).forEach((key) => {
    const f = st.filters[key];
    if (f && f.values && f.values.length)
      active[key] = { values: f.values, mode: f.mode || "include" };
  });
  if (Object.keys(active).length) q.set("filters", JSON.stringify(active));
  const qs = q.toString();
  return qs ? "?" + qs : "";
};

const authenticationService = new AuthenticationService();
// The evaluator is the logged-in Bridge user. Read it from the auth profile's
// employeeProfile (firstName/lastName) — the same source the risk dashboard uses —
// rather than a bare `name` field that isn't present, which left it empty so the
// claim was never actually assigned to the user.
const getEvaluator = () => {
  try {
    const emp = authenticationService.getUserProfile()?.employeeProfile;
    return emp ? [emp.firstName, emp.lastName].filter(Boolean).join(" ").trim() : "";
  } catch (e) {
    return "";
  }
};

function ClaimAIDashboard(props) {
  // Seed every piece of view state from the URL (see readQueryState above), once.
  const initialRef = useRef(null);
  if (!initialRef.current)
    initialRef.current = readQueryState(props.location && props.location.search);
  const init = initialRef.current;

  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageNum, setPageNum] = useState(init.pageNum);
  const [stats, setStats] = useState({});
  const [assigning, setAssigning] = useState("");
  const [reassignRecord, setReassignRecord] = useState(null);

  // search
  const [patientIdInput, setPatientIdInput] = useState(init.search.patientId);
  const [claimIdInput, setClaimIdInput] = useState(init.search.claimId);
  const [nameInput, setNameInput] = useState(init.search.patientName);
  const [search, setSearch] = useState(init.search);

  // multi-select filters: key -> {values:[{id,label}], mode}
  const [filters, setFilters] = useState(init.filters);
  // range/date filters (applied)
  const [received, setReceived] = useState(init.received);
  const [updated, setUpdated] = useState(init.updated);
  const [openFilter, setOpenFilter] = useState("");
  // drafts for the range/date popups (applied on "Apply")
  const [draftReceived, setDraftReceived] = useState(init.received);
  const [draftUpdated, setDraftUpdated] = useState(init.updated);

  const refs = {
    accountCode: useRef(), planId: useRef(), status: useRef(),
    evalStatus: useRef(), evaluator: useRef(), suggestion: useRef(),
    reimbursementType: useRef(), profileType: useRef(),
  };

  // Claim-type filter options (distinct types present in the claims). Small list,
  // fetched once and passed as static options to the column filter.
  const [claimTypeOptions, setClaimTypeOptions] = useState([]);
  useEffect(() => {
    api.fetchDashboardClaimTypes(
      (res) => {
        const list = Array.isArray(res) ? res : (res && res.data) || [];
        setClaimTypeOptions(
          list
            .filter((t) => t && t.id)
            .map((t) => ({ id: String(t.id), label: t.name || String(t.id) }))
        );
      },
      () => setClaimTypeOptions([])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOptions = (field) => (q, offset, limit) =>
    new Promise((resolve) => {
      api.fetchClaimFilterOptions(field, q, offset, limit,
        (res) => resolve(res || { options: [], total: 0, hasMore: false }),
        () => resolve({ options: [], total: 0, hasMore: false }));
    });

  const mf = (key) => {
    const f = filters[key];
    return f && f.values && f.values.length ? { mode: f.mode || "include", values: f.values.map((v) => String(v.id)) } : undefined;
  };
  const prune = (o) => { Object.keys(o).forEach((k) => o[k] === undefined && delete o[k]); return o; };

  const buildListBody = () => {
    const body = {
      pageNum, pageSize: PAGE_SIZE,
      accountCodes: mf("accountCode"), planIds: mf("planId"), statuses: mf("status"),
      reimbursementTypeIds: mf("reimbursementType"), profileTypes: mf("profileType"),
      evaluationStatuses: mf("evalStatus"), evaluators: mf("evaluator"), suggestions: mf("suggestion"),
    };
    if (received.from) body.claimReceivedFrom = received.from;
    if (received.to) body.claimReceivedTo = received.to;
    if (updated.from) body.claimUpdatedFrom = updated.from;
    if (updated.to) body.claimUpdatedTo = updated.to;
    if (search.patientId) body.patientId = Number(search.patientId);
    if (search.claimId) body.claimId = search.claimId;
    if (search.patientName) body.patientName = search.patientName;
    return prune(body);
  };

  const buildStatsBody = () => {
    const f = prune({
      accountCodes: mf("accountCode"), planIds: mf("planId"),
      evaluationStatuses: mf("evalStatus"), evaluators: mf("evaluator"), suggestions: mf("suggestion"),
    });
    return { filters: f };
  };

  const fetchList = (silent) => {
    if (!silent) setLoading(true);
    api.fetchClaimAIDashboard(buildListBody(),
      (res) => { const d = res || {}; setRecords(d.rows || []); setTotalCount(d.totalCount || 0); setLoading(false); },
      () => { if (!silent) { setRecords([]); setTotalCount(0); } setLoading(false); });
  };
  // Keep a live ref so the AI-processing poll always calls the latest closure
  // (current filters/page), not the one captured when the interval was created.
  const fetchListRef = useRef(fetchList);
  fetchListRef.current = fetchList;
  const fetchStats = () => {
    api.fetchClaimDashboardStats(buildStatsBody(), (res) => setStats(res || {}), () => {});
  };

  // Keep the query string in step with the view. `replace` (not `push`) so paging
  // and filtering don't each add a history entry the user has to back through.
  useEffect(() => {
    if (!props.history || !props.location) return;
    const qs = buildQueryString({ pageNum, filters, received, updated, search });
    if (qs !== (props.location.search || ""))
      props.history.replace(props.location.pathname + qs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, filters, received, updated, search]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, filters, received, updated, search]);
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // While any claim on the page is still being processed by AI, poll quietly so
  // the row flips PROCESSING -> Approve/Reject without a manual refresh.
  const anyProcessing = records.some((r) => String(r.aiStatus || "").toUpperCase() === "PROCESSING");
  useEffect(() => {
    if (!anyProcessing) return undefined;
    const t = setInterval(() => fetchListRef.current(true), 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyProcessing]);

  const applyFilter = (key) => (ids, items, mode) => {
    setFilters((p) => ({ ...p, [key]: { values: items.map((it) => ({ id: it.id, label: it.label })), mode } }));
    setPageNum(1); setOpenFilter("");
  };
  // Handed to every screen we push to. `fromDashboard` marks the origin, and
  // `dashboardSearch` carries this list's page/filters (they live in the query
  // string) so those screens can come back to the exact view the user left by
  // pushing this route again — no reliance on popping the history stack, which
  // isn't reliably the dashboard.
  const dashboardState = () => ({
    fromDashboard: true,
    dashboardSearch: (props.location && props.location.search) || "",
  });
  // patientId is threaded into the route so the detail screen can show a graceful
  // "claim details only" view (via the wallet claim) when the claim has no AI record.
  const goDetail = (claimId, patientId, viewOnly) =>
    props.history &&
    props.history.push({
      pathname:
        "/miscellaneous/claims-dashboard/ai-review/" +
        claimId +
        (patientId ? "/" + patientId : ""),
      search: viewOnly ? "?view=1" : "",
      state: dashboardState(),
    });
  // Drafts have no AI record — open them in the File-new-claim screen to resume/submit.
  const isDraft = (r) => Number(r && r.currentStatus) === 9;
  const goEditDraft = (r, viewOnly) =>
    props.history &&
    props.history.push({
      pathname: "/miscellaneous/reimbursement-dashboard/file-claim",
      search:
        "?draftId=" + r.claimId + "&pid=" + r.patientId + (viewOnly ? "&view=1" : ""),
      state: dashboardState(),
    });
  const goFileClaim = () =>
    props.history &&
    props.history.push({
      pathname: "/miscellaneous/reimbursement-dashboard/file-claim",
      state: dashboardState(),
    });
  const evaluate = (row) => {
    setAssigning(row.claimId);
    api.assignClaimEvaluator(row.claimId, { evaluatedBy: getEvaluator() },
      () => { setAssigning(""); goDetail(row.claimId, row.patientId); },
      () => { setAssigning(""); goDetail(row.claimId, row.patientId); });
  };
  // Taking over a claim assigned to someone else — confirmed via the dialog.
  const handleConfirmReassign = () => {
    const row = reassignRecord;
    if (!row || assigning) return;
    setAssigning(row.claimId);
    api.assignClaimEvaluator(
      row.claimId,
      { evaluatedBy: getEvaluator() },
      () => { setAssigning(""); setReassignRecord(null); goDetail(row.claimId, row.patientId); },
      (err) => {
        setAssigning("");
        EventService.emit("showError", { message: (err && err.message) || "Failed to assign evaluator" });
      },
    );
  };

  // Any column filter, date-range filter, or search currently applied.
  const anyFilterApplied =
    Object.keys(filters).some(
      (k) => filters[k] && filters[k].values && filters[k].values.length,
    ) ||
    !!(received.from || received.to || updated.from || updated.to) ||
    !!(search.patientId || search.claimId || search.patientName);

  // Reset every filter/search and reload the full list.
  const clearAllFilters = () => {
    setFilters({});
    setReceived({ from: "", to: "" });
    setUpdated({ from: "", to: "" });
    setDraftReceived({ from: "", to: "" });
    setDraftUpdated({ from: "", to: "" });
    setSearch({ patientId: "", claimId: "", patientName: "" });
    setPatientIdInput("");
    setClaimIdInput("");
    setNameInput("");
    setOpenFilter("");
    setPageNum(1);
  };

  const evaluated = stats.evaluated || 0;
  const pct = (n) => (evaluated ? ((n / evaluated) * 100).toFixed(1) : "0.0");

  const filterTH = (label, key, options, field) => {
    const f = filters[key] || { values: [], mode: "include" };
    const active = f.values && f.values.length > 0;
    return (
      <span className="cd-th">
        {label}
        <img ref={refs[key]} className={"cd-funnel" + (active ? " active" : "")} src={active ? filterApplied : funnelIcon} alt="filter"
          onClick={() => setOpenFilter(openFilter === key ? "" : key)} />
        {openFilter === key && (
          <ColumnFilterPopover
            anchorRef={refs[key]}
            title={"Filter by " + label}
            searchPlaceholder={"Search " + label.toLowerCase() + "..."}
            options={options}
            loadOptions={field ? loadOptions(field) : undefined}
            selectedIds={(f.values || []).map((v) => v.id)}
            selectedItems={f.values || []}
            mode={f.mode}
            onApply={applyFilter(key)}
            onClose={() => setOpenFilter("")}
          />
        )}
      </span>
    );
  };

  // date/confidence range popup column header
  const rangeTH = (label, key, kind, draft, setDraft, applied, setApplied) => {
    const active = kind === "conf" ? applied.min !== "" || applied.max !== "" : !!(applied.from || applied.to);
    const emptyVal = kind === "conf" ? { min: "", max: "" } : { from: "", to: "" };
    // Claim received/updated dates can't be in the future — cap the pickers at today.
    const todayStr = new Date().toLocaleDateString("en-CA"); // local YYYY-MM-DD
    return (
      <span className="cd-th">
        {label}
        <img className={"cd-funnel" + (active ? " active" : "")} src={active ? filterApplied : funnelIcon} alt="filter"
          onClick={() => { setDraft(applied); setOpenFilter(openFilter === key ? "" : key); }} />
        {openFilter === key && (
          <div className="cd-filter-popup" onClick={(e) => e.stopPropagation()}>
            {kind === "conf" ? (
              <React.Fragment>
                <label>Min %</label>
                <input type="number" value={draft.min} onChange={(e) => setDraft({ ...draft, min: e.target.value })} />
                <label>Max %</label>
                <input type="number" value={draft.max} onChange={(e) => setDraft({ ...draft, max: e.target.value })} />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <label>From</label>
                <input type="date" max={todayStr} value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
                <label>To</label>
                <input type="date" max={todayStr} value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
              </React.Fragment>
            )}
            <div className="cd-filter-actions">
              <button className="clear" onClick={() => { setDraft(emptyVal); setApplied(emptyVal); setPageNum(1); setOpenFilter(""); }}>Clear</button>
              <button className="apply" onClick={() => { setApplied(draft); setPageNum(1); setOpenFilter(""); }}>Apply</button>
            </div>
          </div>
        )}
      </span>
    );
  };

  const numFrom = totalCount === 0 ? 0 : (pageNum - 1) * PAGE_SIZE + 1;
  const numTo = Math.min(pageNum * PAGE_SIZE, totalCount);

  return (
    <div className="claim-dashboard-container">
      {/* Header */}
      <div className="cd-header">
        <img
          src={backArrowIcon}
          alt="back"
          className="cd-back"
          onClick={() => props.history && props.history.push("/miscellaneous")}
        />
        <h1>Reimbursement</h1>
        <div className="cd-search-group">
          <div className="cd-search">
            <input placeholder="Patient ID" value={patientIdInput} onChange={(e) => {
              const v = e.target.value.replace(/[^0-9]/g, "");
              setPatientIdInput(v);
              // Clearing the box reloads the full list (reset the applied search).
              if (v === "" && search.patientId) { setSearch((s) => ({ ...s, patientId: "" })); setPageNum(1); }
            }} />
            <button disabled={!patientIdInput.trim()} onClick={() => { setSearch((s) => ({ ...s, patientId: patientIdInput.trim() })); setPageNum(1); }}>Search</button>
          </div>
          <div className="cd-search">
            <input placeholder="Claim ID" value={claimIdInput} onChange={(e) => {
              const v = e.target.value;
              setClaimIdInput(v);
              if (v.trim() === "" && search.claimId) { setSearch((s) => ({ ...s, claimId: "" })); setPageNum(1); }
            }} />
            <button disabled={!claimIdInput.trim()} onClick={() => { setSearch((s) => ({ ...s, claimId: claimIdInput.trim() })); setPageNum(1); }}>Search</button>
          </div>
          <div className="cd-search">
            <input placeholder="Patient name" value={nameInput} onChange={(e) => {
              const v = e.target.value;
              setNameInput(v);
              if (v.trim() === "" && search.patientName) { setSearch((s) => ({ ...s, patientName: "" })); setPageNum(1); }
            }} />
            <button disabled={!nameInput.trim()} onClick={() => { setSearch((s) => ({ ...s, patientName: nameInput.trim() })); setPageNum(1); }}>Search</button>
          </div>
        </div>
        <button className="cd-file-btn" onClick={goFileClaim}>+ File new claim</button>
      </div>

      {/* Stats */}
      <div className="cd-stats">
        {[
          ["Total claims", stats.total || 0, ""],
          ["Pending claims", stats.pending || 0, ""],
          ["AI Accuracy", pct(stats.correct || 0) + "%", "green"],
          ["False Positive", pct(stats.falsePositive || 0) + "%", "orange"],
          ["False Negative", pct(stats.falseNegative || 0) + "%", "red"],
        ].map(([label, value, cls]) => (
          <div className="cd-stat" key={label}>
            <div className="cd-stat-label">{label}</div>
            <div className={"cd-stat-value " + cls}>{value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="cd-table-wrap">
        <table className="cd-table">
          {/* Fixed column widths (sum = 100%) so all 14 columns fit the card — no horizontal scroll (mirrors risk). */}
          <colgroup>
            <col style={{ width: "6%" }} />{/* User type */}
            <col style={{ width: "6%" }} />{/* Patient ID */}
            <col style={{ width: "8%" }} />{/* Claim ID */}
            <col style={{ width: "8%" }} />{/* Claim type */}
            <col style={{ width: "8%" }} />{/* Claim received on */}
            <col style={{ width: "8%" }} />{/* Claim updated on */}
            <col style={{ width: "5%" }} />{/* Invoice amount */}
            <col style={{ width: "7%" }} />{/* Approved claim amount */}
            <col style={{ width: "7%" }} />{/* Account code */}
            <col style={{ width: "5%" }} />{/* Plan ID */}
            <col style={{ width: "9%" }} />{/* AI Suggestion (with confidence) */}
            <col style={{ width: "10%" }} />{/* Evaluator */}
            <col style={{ width: "10%" }} />{/* Status */}
            <col style={{ width: "3%" }} />{/* View */}
          </colgroup>
          <thead>
            <tr>
              <th>{filterTH("User type", "profileType", PROFILE_TYPE_OPTIONS)}</th>
              <th>Patient ID</th>
              <th>Claim ID</th>
              <th>{filterTH("Claim type", "reimbursementType", claimTypeOptions)}</th>
              <th>{rangeTH("Claim received on", "received", "date", draftReceived, setDraftReceived, received, setReceived)}</th>
              <th>{rangeTH("Claim updated on", "updated", "date", draftUpdated, setDraftUpdated, updated, setUpdated)}</th>
              <th>Invoice amount</th>
              <th>Approved claim amount</th>
              <th>{filterTH("Account code", "accountCode", null, "accountCode")}</th>
              <th>{filterTH("Plan ID", "planId", null, "planId")}</th>
              <th>{filterTH("AI Suggestion", "suggestion", SUGGESTION_OPTIONS)}</th>
              <th>{filterTH("Evaluator", "evaluator", null, "evaluator")}</th>
              <th>{filterTH("Status", "status", STATUS_OPTIONS)}</th>
              <th>
                {anyFilterApplied ? (
                  <button
                    className="cd-clear-x-btn"
                    onClick={clearAllFilters}
                    title="Clear all filters"
                  >
                    ×
                  </button>
                ) : null}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={14}><div className="cd-loading">Loading…</div></td></tr>
            ) : records.length === 0 ? (
              <tr><td colSpan={14}><div className="cd-empty">No claims found.</div></td></tr>
            ) : (
              records.map((r, i) => {
                const sugg = String(r.aiSuggestion || "").toUpperCase();
                const aiStatus = String(r.aiStatus || "").toUpperCase();
                const ev = String(r.evaluationStatus || "").toUpperCase();
                const hasEvaluator = !!r.evaluator && ev !== "PENDING";
                return (
                  <tr key={r.claimId || i}>
                    <td>
                      {r.patientProfileType !== undefined && r.patientProfileType !== null ? (
                        <Tooltip
                          title={PROFILE_LABEL[r.patientProfileType] || PROFILE_LABEL[0]}
                          placement="top"
                        >
                          <span
                            className="cd-avatar"
                            style={{ background: PROFILE_BG[r.patientProfileType] || PROFILE_BG[0] }}
                          >
                            <span className="material-icons" style={{ fontSize: 16, lineHeight: 1 }}>person</span>
                          </span>
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </td>
                    {/* The health record, /patient-record/:patientId, in this
                        tab (Nittin, 2026-09-25). A Link rather than the span
                        this was: it pushed the legacy screen from an onClick,
                        which cannot be middle-clicked or copied. .cd-link sets
                        the colour and the underline, so it looks unchanged. */}
                    <td><Link className="cd-link" to={`/patient-record/${r.patientId}`}>{r.patientId}</Link></td>
                    <td>{r.claimId}</td>
                    <td>{r.reimbursementTypeName || "-"}</td>
                    <td>{fmtDate(r.createdAt)}</td>
                    <td>{fmtDate(r.updatedAt)}</td>
                    <td>{rupees(r.claimAmount)}</td>
                    <td>{rupees(r.approvedClaimAmount)}</td>
                    <td>{r.accountCode || "-"}</td>
                    <td>{r.planId || "-"}</td>
                    <td>{aiStatus === "PROCESSING"
                      ? <span className="cd-pill cd-sugg-processing">Processing…</span>
                      : aiStatus === "FAILED"
                      ? <span className="cd-pill cd-sugg-failed">Failed</span>
                      : sugg
                      ? (SUGG_PILL[sugg]
                          ? <span className={"cd-pill " + SUGG_PILL[sugg].cls}>{SUGG_PILL[sugg].label}{Number(r.aiConfidence) > 0 ? " " + Math.round(Number(r.aiConfidence)) + "%" : ""}</span>
                          : <span className="cd-pill">{sugg}{Number(r.aiConfidence) > 0 ? " " + Math.round(Number(r.aiConfidence)) + "%" : ""}</span>)
                      : "-"}</td>
                    <td>
                      {isDraft(r) ? (
                        <button className="cd-evaluate-btn" onClick={() => goEditDraft(r)}>Edit</button>
                      ) : hasEvaluator ? (
                        <span
                          className="cd-evaluator-pill"
                          title={r.evaluator}
                          onClick={() =>
                            r.evaluator === getEvaluator()
                              ? goDetail(r.claimId, r.patientId)
                              : setReassignRecord(r)
                          }
                        >
                          {r.evaluator}
                        </span>
                      ) : (
                        <button className="cd-evaluate-btn" disabled={assigning === r.claimId} onClick={() => evaluate(r)}>
                          {assigning === r.claimId ? "…" : "Evaluate"}
                        </button>
                      )}
                    </td>
                    <td><ClaimStatusCell claim={r} onUpdated={() => { fetchList(); fetchStats(); }} /></td>
                    <td><button className="cd-view" title="View" onClick={() => (isDraft(r) ? goEditDraft(r, true) : goDetail(r.claimId, r.patientId, true))}><img src={eyeOrangeIcon} alt="view" style={{ width: 16, height: 16 }} /></button></td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="cd-pagination">
        <button disabled={pageNum <= 1 || loading} onClick={() => setPageNum(pageNum - 1)}>‹</button>
        <span>Showing <span className="cd-range">{numFrom}-{numTo}</span> of {totalCount}</span>
        <button disabled={loading || pageNum * PAGE_SIZE >= totalCount} onClick={() => setPageNum(pageNum + 1)}>›</button>
      </div>

      {/* Take-over-as-evaluator confirmation (clicking someone else's assignment) */}
      {reassignRecord && (
        <div
          className="assign-evaluator-overlay"
          onClick={() => !assigning && setReassignRecord(null)}
        >
          <div className="assign-evaluator-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="assign-evaluator-icon-wrap">
              <span className="assign-evaluator-icon">!</span>
            </div>
            <h3 className="assign-evaluator-title">Assign yourself as the evaluator?</h3>
            <p className="assign-evaluator-body">
              By confirming, you'll take over as the evaluator for this claim. From this
              point onward:
            </p>
            <ul className="assign-evaluator-list">
              <li>All evaluations on this claim will be attributed to you</li>
              <li>This action is permanent — once assigned, the evaluator cannot be changed</li>
            </ul>
            <div className="assign-evaluator-actions">
              <button
                className="assign-evaluator-cancel"
                disabled={!!assigning}
                onClick={() => setReassignRecord(null)}
              >
                Cancel
              </button>
              <button
                className="assign-evaluator-confirm"
                disabled={!!assigning}
                onClick={handleConfirmReassign}
              >
                {assigning ? "Assigning..." : "Yes, assign to me"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClaimAIDashboard;
